import React, { useMemo } from 'react';
import { View, Text, ScrollView, StyleSheet, Platform } from 'react-native';
import KpiModuleCard from '../charts/KpiModuleCard';
import { calcPackageLeaderboard } from '../kpiService';
import type { PackageRow } from '../kpiService';
import type { KpiDashboardConfig } from '../kpiConfig';

interface Props {
  allData: Record<string, any[]>;
  config:  KpiDashboardConfig;
}

const SCORE_COLORS: Record<string, string> = {
  High:   '#2E7D32',
  Medium: '#F57F17',
  Low:    '#C62828',
};

function ScorePill({ score }: { score: PackageRow['complianceScore'] }) {
  return (
    <View style={[styles.pill, { backgroundColor: SCORE_COLORS[score] + '20', borderColor: SCORE_COLORS[score] + '60' }]}>
      <Text style={[styles.pillText, { color: SCORE_COLORS[score] }]}>{score}</Text>
    </View>
  );
}

const COLS = [
  { key: 'package',           label: 'Package',              width: 140, align: 'left'  as const },
  { key: 'resolutionDays',    label: 'Res. Time (Days)',     width: 110, align: 'center' as const },
  { key: 'wasteDiversion',    label: 'Waste Div. %',         width: 100, align: 'center' as const },
  { key: 'nuisanceFreeDays',  label: 'Nuisance-Free %',      width: 110, align: 'center' as const },
  { key: 'totalBreaches',     label: 'Total Breaches',       width: 100, align: 'center' as const },
  { key: 'trainingManHours',  label: 'Training (hrs)',        width: 110, align: 'center' as const },
  { key: 'complianceScore',   label: 'Compliance Score',     width: 120, align: 'center' as const },
];

export default function PackageLeaderboard({ allData, config }: Props) {
  const rows = useMemo(() => calcPackageLeaderboard(allData, config), [allData, config]);

  if (!rows.length) {
    return (
      <KpiModuleCard title="Masterplan Leaderboard" accentColor="#37474F">
        <Text style={styles.empty}>No package data available for the selected period.</Text>
      </KpiModuleCard>
    );
  }

  return (
    <KpiModuleCard title="Masterplan Leaderboard" accentColor="#37474F">
      <ScrollView horizontal showsHorizontalScrollIndicator={Platform.OS !== 'web'}>
        <View>
          {/* Header */}
          <View style={styles.headerRow}>
            {COLS.map(col => (
              <Text
                key={col.key}
                style={[styles.headerCell, { width: col.width, textAlign: col.align }]}
              >
                {col.label}
              </Text>
            ))}
          </View>

          {/* Rows */}
          {rows.map((row, idx) => (
            <View
              key={row.package}
              style={[styles.dataRow, idx % 2 === 0 ? styles.rowEven : styles.rowOdd]}
            >
              <Text style={[styles.cell, { width: COLS[0].width, fontWeight: '600', color: '#222' }]} numberOfLines={2}>
                {row.package}
              </Text>
              <Text style={[styles.cell, { width: COLS[1].width, textAlign: 'center' }]}>
                {row.resolutionDays.toFixed(1)} d
              </Text>
              <Text style={[styles.cell, { width: COLS[2].width, textAlign: 'center' }]}>
                {row.wasteDiversion}%
              </Text>
              <Text style={[styles.cell, { width: COLS[3].width, textAlign: 'center' }]}>
                {row.nuisanceFreeDays}%
              </Text>
              <Text style={[styles.cell, { width: COLS[4].width, textAlign: 'center' }]}>
                {row.totalBreaches}
              </Text>
              <Text style={[styles.cell, { width: COLS[5].width, textAlign: 'center' }]}>
                {row.trainingManHours.toLocaleString()} hrs
              </Text>
              <View style={[styles.cell, { width: COLS[6].width, alignItems: 'center' }]}>
                <ScorePill score={row.complianceScore} />
              </View>
            </View>
          ))}
        </View>
      </ScrollView>
    </KpiModuleCard>
  );
}

const styles = StyleSheet.create({
  empty: { fontSize: 12, color: '#AAA', textAlign: 'center', paddingVertical: 16 },
  headerRow: {
    flexDirection: 'row',
    backgroundColor: '#ECEFF1',
    paddingVertical: 8,
    paddingHorizontal: 4,
    borderRadius: 6,
    marginBottom: 4,
  },
  headerCell: {
    fontSize: 10,
    fontWeight: '700',
    color: '#555',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    paddingHorizontal: 6,
  },
  dataRow: {
    flexDirection: 'row',
    paddingVertical: 10,
    paddingHorizontal: 4,
    borderRadius: 4,
    alignItems: 'center',
  },
  rowEven: { backgroundColor: '#fff' },
  rowOdd:  { backgroundColor: '#F9F9F9' },
  cell: {
    fontSize: 12,
    color: '#444',
    paddingHorizontal: 6,
  },
  pill: {
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 12,
    borderWidth: 1,
  },
  pillText: {
    fontSize: 11,
    fontWeight: '700',
  },
});
